import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonGroupType } from '../../../models/common';

/**
 * @group Basic Toolkit
 * @component Button
 *
 * @description
 * Button component
 * @export
 * @class LsqButtonComponent
 * @property {string} [type] HTML button type
 * @property {boolean} [disabled] Whether the button is disabled
 * @property {string} [size] One of 'xs', 'sm', 'md', 'lg', or 'block'
 * @property {string} [buttonClass] ClassName to be added to the <button> element
 * @property {boolean} [submitting] Is the button disabled
 * @property {FormGroup} [group] Reference to the FormGroup if type === 'submit'
 * @property {string} [ariaLabel] Accessibility label
 * @property {string} [routerLink] Navigate to this routerLink if present
 * @property {EventEmitter<Event>} [buttonClick] Click handler for the button

 */

@Component({
    selector: 'lsq-button',
    host: {
        '[class.lsq-button-block]': 'isBlock()'
    },
    template: `
        <button
            class="button-{{ appearance }} button-size-{{ size }} {{ buttonGroupType || '' }}"
            [class.disabled]="disabled"
            [ngClass]="buttonClass"
            [attr.type]="type"
            (click)="handleClick($event)"
            [attr.tabindex]="tabindex"
            [attr.aria-label]="ariaLabel"
            [disabled]="disabled || submitting"
        >
            <span [class.is-hidden]="submitting">
                <ng-content></ng-content>
            </span>
            <lsq-button-spinner *ngIf="submitting"></lsq-button-spinner>
        </button>
    `,
    styleUrls: ['./lsq-button.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqButtonComponent {
    @Input() public type: 'button' | 'submit' = 'button';
    @Input() public appearance:
        | 'circle'
        | 'circle-secondary'
        | 'primary'
        | 'secondary'
        | 'icon'
        | 'transparent' = 'primary';
    @Input() public disabled = false;
    @Input() public size: 'sm' | 'md' | 'lg' | 'auto' | 'block' = 'auto';
    @Input() public buttonClass = '';
    @Input() public submitting?: boolean;
    @Input() public group?: FormGroup;
    @Input() public ariaLabel?: string;
    @Input() public routerLink?: string;
    @Input() public tabindex?: string;
    @Input() public buttonGroupType?: ButtonGroupType;
    @Output() public click = new EventEmitter<Event>();
    @Output() public buttonClick = new EventEmitter<Event>();

    constructor(private _router: Router) {}

    private _validateAllFormFields(formGroup: FormGroup | FormArray): void {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            if (control instanceof FormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof FormGroup) {
                this._validateAllFormFields(control);
            } else if (control instanceof FormArray) {
                this._validateAllFormFields(control);
            }
        });
    }

    public handleClick(event: Event): void {
        event.stopImmediatePropagation();

        if (this.type === 'submit') {
            if (this.group && !this.group.valid) {
                event.preventDefault();
                this._validateAllFormFields(this.group);
                this.submitting = false;
                return;
            }
        }

        if (this.routerLink) {
            this._router.navigateByUrl(this.routerLink);
        }

        this.buttonClick.emit(event);
        if (this.click.observers.length) {
            // Using the `click` event will ignore the `disabled` Input.
            console.warn(
                'LsqButtonComponent: Deprecated usage of the (click) DOM event. Please use (buttonClick) instead.'
            );
            this.click.emit(event);
        }
    }

    public isBlock(): boolean {
        return this.size === 'block';
    }
}
